// Cabin preferences on the booking page: the exact temperature in °F (62 to 78, 1° steps, 71 by default) and the music choice.
// Kept compatible with the apps and the server: the old temperature words (Cooler / No preference / Warmer) are still sent, derived from the exact value.
export const TEMP_MIN = 62;
export const TEMP_MAX = 78;
export const TEMP_DEFAULT = 71;
export const ARC_DEG = 135;                           // the dial sweeps from -135 to +135 degrees around the top (a 270 degree arc)

export const clampTemp = (t) => Math.max(TEMP_MIN, Math.min(TEMP_MAX, Math.round(Number(t))));

// The label next to "Cabin temperature": 68 and below Cool, 69 to 73 Neutral, 74 and above Warm.
export const tempState = (t) => (t <= 68 ? 'Cool' : t >= 74 ? 'Warm' : 'Neutral');
// The OLD three-word field the apps and the server already know.
export const tempWord = (t) => (t <= 68 ? 'Cooler' : t >= 74 ? 'Warmer' : 'No preference');

export const angleForTemp = (t) => -ARC_DEG + ((clampTemp(t) - TEMP_MIN) / (TEMP_MAX - TEMP_MIN)) * 2 * ARC_DEG;

// A pointer angle (degrees clockwise from the top, -180..180) to a whole degree F; the open part at the bottom snaps to the nearer end.
export function tempForAngle(deg) {
  if (deg > ARC_DEG) return TEMP_MAX;
  if (deg < -ARC_DEG) return TEMP_MIN;
  return clampTemp(TEMP_MIN + ((deg + ARC_DEG) / (2 * ARC_DEG)) * (TEMP_MAX - TEMP_MIN));
}

// Blue (cool) through white to orange (warm).
const STOPS = [[96, 165, 250], [243, 244, 246], [249, 115, 22]];
export function tickColor(t) {
  const x = (clampTemp(t) - TEMP_MIN) / (TEMP_MAX - TEMP_MIN);
  const [a, b, f] = x < 0.5 ? [STOPS[0], STOPS[1], x / 0.5] : [STOPS[1], STOPS[2], (x - 0.5) / 0.5];
  return `rgb(${a.map((v, i) => Math.round(v + (b[i] - v) * f)).join(',')})`;
}

export const MUSIC_OPTIONS = ['No music', "Chauffeur's choice", 'My audio'];
const MUSIC_OLD = { 'play music': "Chauffeur's choice", silence: 'No music' };
export const cleanMusic = (v) => {
  const s = String(v || '').replace('’', "'").trim();
  return MUSIC_OPTIONS.find((o) => o.toLowerCase() === s.toLowerCase()) || MUSIC_OLD[s.toLowerCase()] || null;
};

export const PREFERENCE_TOGGLES = [{ key: 'conversation', label: 'Conversation', options: ['Happy to chat', 'Quiet ride'] }];
export const DEFAULT_PREFERENCES = { conversation: 'Quiet ride', music: "Chauffeur's choice", cabin_temp_f: TEMP_DEFAULT, temperature: 'No preference' };

// What the page starts with: the profile's preferences (old or new), with the old words and music values mapped to the new ones.
// Anything else the profile holds (gratuity, luggage help) is carried along untouched.
export function prefsFromProfile(profile) {
  const p = { ...(profile || {}) };
  let t = Number(p.cabin_temp_f);
  if (!Number.isFinite(t) || p.cabin_temp_f === '' || p.cabin_temp_f == null) {
    t = p.temperature === 'Cooler' ? 66 : p.temperature === 'Warmer' ? 76 : TEMP_DEFAULT;       // only an old word: a typical value for it
  }
  const cabin_temp_f = clampTemp(t);
  return { ...DEFAULT_PREFERENCES, ...p, cabin_temp_f, temperature: tempWord(cabin_temp_f), music: cleanMusic(p.music) || DEFAULT_PREFERENCES.music };
}

// What is sent with a booking: the exact temperature, the old word derived from it, the music and the rest.
export const prefsForBooking = (prefs) => {
  const base = prefsFromProfile(prefs);
  return { ...base, cabin_temp_f: base.cabin_temp_f, temperature: tempWord(base.cabin_temp_f) };
};

export function prefsSummary(prefs) {
  const p = prefsFromProfile(prefs);
  return [p.conversation, p.music, `${p.cabin_temp_f}°F`].filter(Boolean).join(' · ');
}
